import styled from 'styled-components';
import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';

import Button from 'design-system/src/components/Button/Button';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast.tsx';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip.tsx';

import { client } from '../../api';
import type { OrgMember } from '../../api';
import { AuthContext } from '../../auth';
import { Input } from '../Input';
import { ControllerWrapper } from '../signup/ControllerWrapper.tsx';
import { DropdownWithPlaceholder } from '../Dropdown.tsx';
import { getRoleOptions } from '../signup/utils.tsx';
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '../../constants.ts';
import { requiredErrorMessage, requiredErrorString, formatPhoneNumberIncrementally } from '../../utils.tsx';
import { FormLabel } from '../FormLabel.tsx';

const Form = styled.form`
  max-width: var(--main-content-width);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WhiteInput = styled(Input)`
  background-color: white;
`;

const WhiteDropdown = styled(DropdownWithPlaceholder)`
  background-color: white;
`;

const SubmitButton = styled(Button)`
  margin-top: 8px;
`;

type FormData = {
  first_name: string;
  last_name: string;
  email_address: string;
  role: string;
  contact_phone: string;
};

/**
 * User profile page.
 *
 * All users can see this page and edit their own name, but only admins can edit email addresses.
 */
const UsersTable: React.FC = () => {
  const authContext = useContext(AuthContext);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const queryClient = useQueryClient();

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      // the default values are set after the initial data is fetched
      first_name: '',
      last_name: '',
      email_address: '',
      role: '',
      contact_phone: '',
    },
  });

  const memberQuery = useQuery<{ member: OrgMember }>({
    queryKey: ['orgMember', authContext.user?.email],
    queryFn: () => {
      return client.get('org-member/current-user').then((res) => {
        const splitName = (res.data.member.name ?? '').split(' ');
        setValue('first_name', splitName[0] || '');
        setValue('last_name', splitName[1] || '');
        setValue('email_address', res.data.member.email_address);
        setValue('role', res.data.member.job_title || '');
        setValue('contact_phone', res.data.member.contact_phone || '');
        return res.data;
      });
    },
  });

  const submit = useMutation<{ success: boolean }, AxiosError<{ error: string }>, FormData>({
    mutationFn: async (data) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking editing user data, no real request sent.');
        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      const submitData = {
        ...data,
        role: memberQuery.data?.member.is_admin ? 'stytch_admin' : 'stytch_member',
        job_title: data.role || undefined,
        contact_phone: data.contact_phone || '',
      };

      return client.post(`org-members/edit/${memberQuery.data?.member.id}`, submitData).then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orgMember'] });
      toast.custom(() => <Toast variant="success">Profile updated</Toast>);
    },
    onError: async (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  let body;
  if (memberQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (memberQuery.error) {
    body = <PageLoadError />;
  } else if (!memberQuery.data) {
    body = null;
  } else {
    body = (
      <Form onSubmit={handleSubmit((data: FormData) => submit.mutate(data))}>
        <ControllerWrapper>
          <Controller
            control={control}
            name="first_name"
            rules={{ required: true }}
            render={({ field }) => (
              <WhiteInput
                label="First name"
                autoComplete="given-name"
                hasError={!!errors.first_name}
                disabled={submit.isPending}
                {...field}
              />
            )}
          />
          {errors.first_name && requiredErrorMessage}
        </ControllerWrapper>

        <ControllerWrapper>
          <Controller
            control={control}
            name="last_name"
            rules={{ required: true }}
            render={({ field }) => (
              <WhiteInput
                label="Last name"
                autoComplete="family-name"
                hasError={!!errors.last_name}
                disabled={submit.isPending}
                {...field}
              />
            )}
          />
          {errors.last_name && requiredErrorMessage}
        </ControllerWrapper>

        {memberQuery.data?.member.is_admin ? (
          <ControllerWrapper>
            <Controller
              control={control}
              name="email_address"
              rules={{
                required: { value: true, message: requiredErrorString },
                pattern: {
                  value: EMAIL_VALIDATION_REGEX,
                  message: 'Invalid email',
                },
              }}
              render={({ field }) => (
                <WhiteInput
                  label="Email"
                  autoComplete="email"
                  hasError={!!errors.email_address}
                  disabled={submit.isPending}
                  {...field}
                />
              )}
            />
            {errors.email_address && <ErrorMessage>{errors.email_address.message}</ErrorMessage>}
          </ControllerWrapper>
        ) : (
          <Tooltip content="Only admins can edit user email addresses.">
            <div tabIndex={0}>
              <Controller
                control={control}
                name="email_address"
                disabled={true}
                render={({ field }) => <WhiteInput label="Email" autoComplete="email" {...field} />}
              />
            </div>
          </Tooltip>
        )}

        <ControllerWrapper>
          <Controller
            control={control}
            name="contact_phone"
            rules={{
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Please enter a valid phone number',
              },
            }}
            render={({ field }) => (
              <WhiteInput
                type="tel"
                label="Contact phone"
                autoComplete="tel"
                hasError={!!errors.contact_phone}
                disabled={submit.isPending}
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhoneNumberIncrementally(e.target.value);
                  field.onChange(formatted);
                }}
              />
            )}
          />
          {errors.contact_phone && <ErrorMessage>{errors.contact_phone.message}</ErrorMessage>}
        </ControllerWrapper>

        <ControllerWrapper>
          <FormLabel htmlFor="role">Role</FormLabel>
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <WhiteDropdown id="role" placeholder="Select a role" disabled={submit.isPending} {...field}>
                {getRoleOptions()}
              </WhiteDropdown>
            )}
          />
        </ControllerWrapper>

        <SubmitButton type="submit" size="lg" isLoading={submit.isPending} disabled={submit.isPending}>
          Save information
        </SubmitButton>

        <ErrorMessage visible={submit.isError}>{submit.error?.response?.data.error}</ErrorMessage>
      </Form>
    );
  }

  return body;
};

export default UsersTable;
